import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiClient } from '@/api/client';
import { useAuth } from '@/context/auth';
import { toast } from '@/components/ui/use-toast';
import { hasSuperAdminAccess, hasOrgAdminAccess } from '@/context/auth/types';

// Type definitions
export interface RestaurantSmsStatus {
  restaurantId: string;
  enabled: boolean;
  organisationId?: string;
  lastChecked?: Date;
}

export interface OrganisationSmsStatus {
  organisationId: string;
  enabled: boolean;
  lastChecked?: Date;
}

export interface SmsUsage {
  currentPeriod: {
    units: number;
    message_count: number;
    start_date: string;
    estimated_cost: string;
  };
  history: any[]; // Can be expanded later
}

export interface RestaurantWithSmsStatus {
  id: string;
  name: string | null | undefined;  // Updated to allow null/undefined
  smsEnabled: boolean;
  organisationId: string;
}

interface SmsStatusContextType {
  // Status state
  restaurantSmsStatus: Map<string, boolean>;
  organisationSmsStatus: Map<string, boolean>;
  
  // Loading and error states
  loading: boolean;
  error: string | null;
  
  // Permission functions
  canEnableSms: (user: any, restaurantId: string) => boolean;
  canDisableSms: (user: any, restaurantId: string) => boolean;
  canViewSmsStatus: (user: any, restaurantId: string) => boolean;
  
  // Status check functions
  checkRestaurantSmsStatus: (restaurantId: string) => Promise<boolean>;
  checkOrganisationSmsStatus: (organisationId: string) => Promise<boolean>;
  
  // Operation functions
  enableRestaurantSms: (restaurantId: string) => Promise<boolean>;
  disableRestaurantSms: (restaurantId: string) => Promise<boolean>;
  enableOrganisationSms: (organisationId: string) => Promise<boolean>;
  disableOrganisationSms: (organisationId: string) => Promise<boolean>;
  
  // Usage functions
  getRestaurantSmsUsage: (restaurantId: string) => Promise<SmsUsage | null>;
  getOrganisationSmsUsage: (organisationId: string) => Promise<SmsUsage | null>;
  
  // Helper functions
  getRestaurantListWithSmsStatus: (organisationId: string) => Promise<RestaurantWithSmsStatus[]>;
  
  // Cache management
  clearCache: () => void;
}

// Create context with default value
const SmsStatusContext = createContext<SmsStatusContextType | undefined>(undefined);

// Cache TTL (in milliseconds)
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const SmsStatusProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const { user } = useAuth();
  
  // State for caching statuses
  const [restaurantSmsStatus, setRestaurantSmsStatus] = useState<Map<string, boolean>>(new Map());
  const [organisationSmsStatus, setOrganisationSmsStatus] = useState<Map<string, boolean>>(new Map());
  const [statusCache, setStatusCache] = useState<Map<string, {value: boolean, timestamp: number}>>(new Map());
  const [usageCache, setUsageCache] = useState<Map<string, {value: SmsUsage, timestamp: number}>>(new Map());
  
  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Cache management
  const clearCache = useCallback(() => {
    setStatusCache(new Map());
    setUsageCache(new Map());
    setRestaurantSmsStatus(new Map());
    setOrganisationSmsStatus(new Map());
  }, []);
  
  /**
   * Permission checks
   */
  const canEnableSms = useCallback((user: any, restaurantId: string): boolean => {
    if (!user) return false;
    
    // Super admins can enable for any restaurant
    if (hasSuperAdminAccess(user.role)) return true;
    
    // Get restaurant's organisation
    // This is a simple implementation - in a real app, you'd need to look up the restaurant's org
    const restaurantOrg = user.restaurants?.find(r => r.id === restaurantId)?.organisationId;
    
    // Org admins can enable for restaurants in their organisation
    if (hasOrgAdminAccess(user.role) && user.organisationId === restaurantOrg) {
      return true;
    }
    
    // Restaurant admins and users cannot enable
    return false;
  }, []);
  
  const canDisableSms = useCallback((user: any, restaurantId: string): boolean => {
    // Only super admins can disable
    return user ? hasSuperAdminAccess(user.role) : false;
  }, []);
  
  const canViewSmsStatus = useCallback((user: any, restaurantId: string): boolean => {
    if (!user) return false;
    
    // Any authenticated user can view status of restaurants they have access to
    return !!user.restaurants?.some(r => r.id === restaurantId);
  }, []);
  
  /**
   * Status check functions with caching
   */
  const checkRestaurantSmsStatus = useCallback(async (restaurantId: string): Promise<boolean> => {
    if (!restaurantId) return false;
    
    // Check cache first
    const cacheKey = `restaurant_${restaurantId}`;
    const cachedStatus = statusCache.get(cacheKey);
    
    if (cachedStatus && (Date.now() - cachedStatus.timestamp) < CACHE_TTL) {
      setRestaurantSmsStatus(prev => new Map(prev).set(restaurantId, cachedStatus.value));
      return cachedStatus.value;
    }
    
    // Cache miss or expired, fetch from API
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/api/restaurant-sms/status/${restaurantId}`);
      const enabled = response.data?.enabled || false;
      
      // Update cache
      setStatusCache(prev => new Map(prev).set(cacheKey, {
        value: enabled,
        timestamp: Date.now()
      }));
      
      // Update state
      setRestaurantSmsStatus(prev => new Map(prev).set(restaurantId, enabled));
      
      return enabled;
    } catch (err: any) {
      // Handle 401 - auth failure, let apiClient redirect without interference
      if (err.response?.status === 401) {
        console.warn('Auth session expired - stopping all restaurant SMS status checks');
        clearCache();
        // Return immediately without setting error state to prevent re-renders
        return false;
      }

      console.error('Error checking restaurant SMS status:', err);
      setError(err.message || 'Failed to check restaurant SMS status');
      return false;
    } finally {
      setLoading(false);
    }
  }, [statusCache]);
  
  const checkOrganisationSmsStatus = useCallback(async (organisationId: string): Promise<boolean> => {
    if (!organisationId) return false;
    
    // Check cache first
    const cacheKey = `organisation_${organisationId}`;
    const cachedStatus = statusCache.get(cacheKey);
    
    if (cachedStatus && (Date.now() - cachedStatus.timestamp) < CACHE_TTL) {
      setOrganisationSmsStatus(prev => new Map(prev).set(organisationId, cachedStatus.value));
      return cachedStatus.value;
    }
    
    // Cache miss or expired, fetch from API
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/api/sms-billing/status/${organisationId}`);
      const enabled = response.data?.enabled || false;
      
      // Update cache
      setStatusCache(prev => new Map(prev).set(cacheKey, {
        value: enabled,
        timestamp: Date.now()
      }));
      
      // Update state
      setOrganisationSmsStatus(prev => new Map(prev).set(organisationId, enabled));
      
      return enabled;
    } catch (err: any) {
      // Handle 401 - auth failure, let apiClient redirect without interference
      if (err.response?.status === 401) {
        console.warn('Auth session expired - stopping all organisation SMS status checks');
        clearCache();
        // Return immediately without setting error state to prevent re-renders
        return false;
      }

      console.error('Error checking organisation SMS status:', err);
      setError(err.message || 'Failed to check organisation SMS status');
      return false;
    } finally {
      setLoading(false);
    }
  }, [statusCache]);
  
  /**
   * Operation functions with permission checks
   */
  const enableRestaurantSms = useCallback(async (restaurantId: string): Promise<boolean> => {
    if (!restaurantId || !user) return false;
    
    // Check permission
    if (!canEnableSms(user, restaurantId)) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to enable SMS for this restaurant",
        variant: "destructive"
      });
      return false;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Note: We're not passing stripe_customer_id here since this is a general-purpose
      // enable function used in contexts where we don't have the customer ID.
      // For Stripe integration, use the component-specific functions that pass customer IDs.
      const response = await apiClient.post(`/api/restaurant-sms/enable/${restaurantId}`);
      const success = response.data?.success || false;
      
      if (success) {
        // Update cache and state
        const cacheKey = `restaurant_${restaurantId}`;
        setStatusCache(prev => new Map(prev).set(cacheKey, {
          value: true,
          timestamp: Date.now()
        }));
        setRestaurantSmsStatus(prev => new Map(prev).set(restaurantId, true));
        
        toast({
          title: "SMS Enabled",
          description: "SMS has been successfully enabled for this restaurant",
        });
      } else {
        toast({
          title: "Failed to Enable SMS",
          description: response.data?.message || "Unknown error occurred",
          variant: "destructive"
        });
      }
      
      return success;
    } catch (err: any) {
      console.error('Error enabling restaurant SMS:', err);
      setError(err.message || 'Failed to enable restaurant SMS');
      
      toast({
        title: "Error",
        description: err.message || "Failed to enable SMS for this restaurant",
        variant: "destructive"
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, canEnableSms]);
  
  const disableRestaurantSms = useCallback(async (restaurantId: string): Promise<boolean> => {
    if (!restaurantId || !user) return false;
    
    // Check permission
    if (!canDisableSms(user, restaurantId)) {
      toast({
        title: "Permission Denied",
        description: "Only Super Admins can disable SMS features",
        variant: "destructive"
      });
      return false;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.post(`/api/restaurant-sms/disable/${restaurantId}`);
      const success = response.data?.success || false;
      
      if (success) {
        // Update cache and state
        const cacheKey = `restaurant_${restaurantId}`;
        setStatusCache(prev => new Map(prev).set(cacheKey, {
          value: false,
          timestamp: Date.now()
        }));
        setRestaurantSmsStatus(prev => new Map(prev).set(restaurantId, false));
        
        toast({
          title: "SMS Disabled",
          description: "SMS has been successfully disabled for this restaurant",
        });
      } else {
        toast({
          title: "Failed to Disable SMS",
          description: response.data?.message || "Unknown error occurred",
          variant: "destructive"
        });
      }
      
      return success;
    } catch (err: any) {
      console.error('Error disabling restaurant SMS:', err);
      setError(err.message || 'Failed to disable restaurant SMS');
      
      toast({
        title: "Error",
        description: err.message || "Failed to disable SMS for this restaurant",
        variant: "destructive"
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, canDisableSms]);
  
  const enableOrganisationSms = useCallback(async (organisationId: string): Promise<boolean> => {
    if (!organisationId || !user) return false;
    
    // Check permission (simplified - use actual permission logic)
    if (!hasSuperAdminAccess(user.role) && 
        !(hasOrgAdminAccess(user.role) && user.organisationId === organisationId)) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to enable SMS for this organisation",
        variant: "destructive"
      });
      return false;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.post(`/api/sms-billing/enable/${organisationId}`);
      const success = response.data?.success || false;
      
      if (success) {
        // Update cache and state
        const cacheKey = `organisation_${organisationId}`;
        setStatusCache(prev => new Map(prev).set(cacheKey, {
          value: true,
          timestamp: Date.now()
        }));
        setOrganisationSmsStatus(prev => new Map(prev).set(organisationId, true));
        
        toast({
          title: "SMS Enabled",
          description: "SMS has been successfully enabled for this organisation",
        });
      } else {
        toast({
          title: "Failed to Enable SMS",
          description: response.data?.message || "Unknown error occurred",
          variant: "destructive"
        });
      }
      
      return success;
    } catch (err: any) {
      console.error('Error enabling organisation SMS:', err);
      setError(err.message || 'Failed to enable organisation SMS');
      
      toast({
        title: "Error",
        description: err.message || "Failed to enable SMS for this organisation",
        variant: "destructive"
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  const disableOrganisationSms = useCallback(async (organisationId: string): Promise<boolean> => {
    if (!organisationId || !user) return false;
    
    // Only super admins can disable
    if (!hasSuperAdminAccess(user.role)) {
      toast({
        title: "Permission Denied",
        description: "Only Super Admins can disable SMS features",
        variant: "destructive"
      });
      return false;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.post(`/api/sms-billing/disable/${organisationId}`);
      const success = response.data?.success || false;
      
      if (success) {
        // Update cache and state
        const cacheKey = `organisation_${organisationId}`;
        setStatusCache(prev => new Map(prev).set(cacheKey, {
          value: false,
          timestamp: Date.now()
        }));
        setOrganisationSmsStatus(prev => new Map(prev).set(organisationId, false));
        
        toast({
          title: "SMS Disabled",
          description: "SMS has been successfully disabled for this organisation",
        });
      } else {
        toast({
          title: "Failed to Disable SMS",
          description: response.data?.message || "Unknown error occurred",
          variant: "destructive"
        });
      }
      
      return success;
    } catch (err: any) {
      console.error('Error disabling organisation SMS:', err);
      setError(err.message || 'Failed to disable organisation SMS');
      
      toast({
        title: "Error",
        description: err.message || "Failed to disable SMS for this organisation",
        variant: "destructive"
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  /**
   * Usage data functions with caching
   */
  const getRestaurantSmsUsage = useCallback(async (restaurantId: string): Promise<SmsUsage | null> => {
    // Add validation to prevent calls with invalid IDs
    if (!restaurantId || restaurantId === 'unknown-id' || restaurantId === 'undefined') {
      console.warn(`Skipping SMS usage fetch for invalid restaurant ID: "${restaurantId}"`);
      return null;
    }
    
    // Check cache first
    const cacheKey = `restaurant_usage_${restaurantId}`;
    const cachedUsage = usageCache.get(cacheKey);
    
    if (cachedUsage && (Date.now() - cachedUsage.timestamp) < CACHE_TTL) {
      return cachedUsage.value;
    }
    
    // Cache miss or expired, fetch from API
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Fetching SMS usage for restaurant: ${restaurantId}`);
      const response = await apiClient.get(`/api/restaurant-sms/usage/${restaurantId}`);
      
      // Log API response for debugging
      console.log(`SMS usage API response for ${restaurantId}:`, response.data);
      
      if (response.data?.success && response.data?.usage) {
        const usage = response.data.usage as SmsUsage;
        
        // Update cache
        setUsageCache(prev => new Map(prev).set(cacheKey, {
          value: usage,
          timestamp: Date.now()
        }));
        
        return usage;
      }
      
      return null;
    } catch (err: any) {
      // Handle 401 - auth failure, let apiClient redirect without interference
      if (err.response?.status === 401) {
        console.warn('Auth session expired - stopping all restaurant SMS usage checks');
        clearCache();
        // Return immediately without setting error state to prevent re-renders
        return null;
      }

      console.error(`Error fetching restaurant SMS usage for ${restaurantId}:`, err);
      setError(err.message || 'Failed to fetch restaurant SMS usage');
      return null;
    } finally {
      setLoading(false);
    }
  }, [usageCache]);
  
  const getOrganisationSmsUsage = useCallback(async (organisationId: string): Promise<SmsUsage | null> => {
    if (!organisationId) return null;
    
    // Check cache first
    const cacheKey = `organisation_usage_${organisationId}`;
    const cachedUsage = usageCache.get(cacheKey);
    
    if (cachedUsage && (Date.now() - cachedUsage.timestamp) < CACHE_TTL) {
      return cachedUsage.value;
    }
    
    // Cache miss or expired, fetch from API
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/api/sms-billing/usage/${organisationId}`);
      
      if (response.data?.success && response.data?.usage) {
        const usage = response.data.usage as SmsUsage;
        
        // Update cache
        setUsageCache(prev => new Map(prev).set(cacheKey, {
          value: usage,
          timestamp: Date.now()
        }));
        
        return usage;
      }
      
      return null;
    } catch (err: any) {
      // Handle 401 - auth failure, let apiClient redirect without interference
      if (err.response?.status === 401) {
        console.warn('Auth session expired - stopping all organisation SMS usage checks');
        clearCache();
        // Return immediately without setting error state to prevent re-renders
        return null;
      }

      console.error('Error fetching organisation SMS usage:', err);
      setError(err.message || 'Failed to fetch organisation SMS usage');
      return null;
    } finally {
      setLoading(false);
    }
  }, [usageCache]);
  
  /**
   * Helper function to get all restaurants with SMS status
   */
  const getRestaurantListWithSmsStatus = useCallback(async (organisationId: string): Promise<RestaurantWithSmsStatus[]> => {
    if (!organisationId) {
      console.warn('getRestaurantListWithSmsStatus called with empty organisationId');
      return [];
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Requesting restaurant list for org: ${organisationId}`);
      const response = await apiClient.get(`/api/restaurant-sms/org/${organisationId}`);
      
      // Log the raw API response for debugging
      console.log('Restaurant list API response:', response.data);
      
      if (response.data?.restaurants && Array.isArray(response.data.restaurants)) {
        const restaurants = response.data.restaurants;
        
        // Validate each restaurant object
        const validatedRestaurants = restaurants
          .map(restaurant => {
            // Ensure we have a valid restaurant object
            if (!restaurant) {
              console.warn('Null restaurant in API response');
              return null;
            }
            
            // Log any restaurants with missing properties
            if (!restaurant.id || !restaurant.name) {
              console.warn('Restaurant with missing properties:', restaurant);
            }
            
            // Convert to our internal format, preserving all properties
            return {
              id: restaurant.restaurant_id || restaurant.id || 'unknown-id',
              name: restaurant.restaurant_name || restaurant.name || '',
              smsEnabled: typeof restaurant.sms_enabled !== 'undefined' ? restaurant.sms_enabled : false,
              organisationId: restaurant.organisation_id || ''
            };
          })
          .filter(Boolean) as RestaurantWithSmsStatus[];
        
        // Log the transformed data
        console.log('Transformed restaurant data:', validatedRestaurants);
        
        // Update our status caches with this data
        validatedRestaurants.forEach(restaurant => {
          if (restaurant && restaurant.id && restaurant.id !== 'unknown-id') {
            const cacheKey = `restaurant_${restaurant.id}`;
            setStatusCache(prev => new Map(prev).set(cacheKey, {
              value: restaurant.smsEnabled,
              timestamp: Date.now()
            }));
            setRestaurantSmsStatus(prev => new Map(prev).set(restaurant.id, restaurant.smsEnabled));
          }
        });
        
        return validatedRestaurants;
      }
      
      console.warn('No restaurants data in API response:', response.data);
      return [];
    } catch (err: any) {
      // Handle 401 - auth failure, let apiClient redirect without interference
      if (err.response?.status === 401) {
        console.warn('Auth session expired - stopping restaurant list fetch');
        clearCache();
        // Return immediately without setting error state to prevent re-renders
        return [];
      }

      console.error('Error fetching restaurants with SMS status:', err);
      setError(err.message || 'Failed to fetch restaurants with SMS status');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Context value
  const contextValue: SmsStatusContextType = {
    // State
    restaurantSmsStatus,
    organisationSmsStatus,
    loading,
    error,
    
    // Permission functions
    canEnableSms,
    canDisableSms,
    canViewSmsStatus,
    
    // Status check functions
    checkRestaurantSmsStatus,
    checkOrganisationSmsStatus,
    
    // Operation functions
    enableRestaurantSms,
    disableRestaurantSms,
    enableOrganisationSms,
    disableOrganisationSms,
    
    // Usage functions
    getRestaurantSmsUsage,
    getOrganisationSmsUsage,
    
    // Helper functions
    getRestaurantListWithSmsStatus,
    
    // Cache management
    clearCache
  };

  return (
    <SmsStatusContext.Provider value={contextValue}>
      {children}
    </SmsStatusContext.Provider>
  );
};

// Custom hook to use the sms status context
export const useSmsStatus = () => {
  const context = useContext(SmsStatusContext);
  
  if (context === undefined) {
    throw new Error('useSmsStatus must be used within a SmsStatusProvider');
  }
  
  return context;
};
